import ChartCard from "./ChartCard";
import KpiCard from "./KpiCard";
import ManagementAttention from "./ManagementAttention";
import { AfLineChart, AnnualBars, CashChart, StepChart } from "./charts";
import { MONTH_LABELS, fmtMonths, fmtPct, fmtRate, fmtSEK, splitActualForecast } from "./dashUtils";

export default function CompanyDashboard({ data }) {
  const months = data.months.map((m) => ({ ...m, label: MONTH_LABELS[m.month - 1] }));
  const cum = [];
  let sRun = 0, pRun = 0;
  for (const m of months) {
    sRun += m.sales || 0;
    pRun += m.pbt || 0;
    cum.push({ ...m, sales: sRun, pbt: pRun });
  }
  const t = data.targets;
  const s = data.summary;
  const survTone = t.survival_months != null && s.survival_months != null && s.survival_months < t.survival_months ? "error" : "default";

  return (
    <div className="space-y-4" data-testid="company-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="company-summary">
        <KpiCard testId="kpi-expected-sales" label="Expected Sales" value={`${fmtSEK(s.expected_sales)} kr`} sub={`Full year ${data.year} (actual + forecast)`} />
        <KpiCard testId="kpi-expected-pbt" label="Expected PBT" value={`${fmtSEK(s.expected_pbt)} kr`} sub="Profit before tax" />
        <KpiCard testId="kpi-net-margin" label="Expected Net Margin" value={fmtPct(s.expected_net_margin)}
                 sub={t.net_margin != null ? `Target ${fmtPct(t.net_margin)} (PBT/Sales)` : "Target not configured"}
                 tone={t.net_margin != null && s.expected_net_margin != null && s.expected_net_margin < t.net_margin ? "warning" : "default"} />
        <KpiCard testId="kpi-gross-margin" label="Gross Margin" value={fmtPct(s.gross_margin)}
                 sub={t.gross_margin != null ? `Target ${fmtPct(t.gross_margin)}` : "Target not configured"} />
        <KpiCard testId="kpi-closing-cash" label="Year-end Closing Cash" value={`${fmtSEK(s.year_end_closing_cash)} kr`} sub="December closing balance" />
        <KpiCard testId="kpi-survival" label="Survival" value={s.survival_months != null ? `${fmtMonths(s.survival_months)} months` : "N/A"}
                 sub={t.survival_months != null ? `Target ${fmtMonths(t.survival_months)} months` : "Target not configured"} tone={survTone} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <ChartCard title="Sales & PBT" subtitle="Cumulative — solid: actual, dashed: forecast" linkTo="/financials/pnl" linkLabel="P&L" testId="chart-sales-pbt">
          <AfLineChart months={cum} testId="chart-sales-pbt-c"
                       formatter={(v) => `${Math.round(v / 1000)}k`}
                       series={[{ key: "sales", label: "Sales", color: "#0052CC" }, { key: "pbt", label: "PBT", color: "#f59e0b" }]} />
        </ChartCard>

        <ChartCard title="Cash Flow & Closing Cash" subtitle="Monthly net flow and closing balance" linkTo="/financials/cash-flow" linkLabel="Cash Flow" testId="chart-cash">
          <CashChart months={months} testId="chart-cash-c" />
        </ChartCard>

        <ChartCard title="Profitability" subtitle="Cumulative YTD margins vs configured targets" linkTo="/financials/pnl" linkLabel="P&L" testId="chart-profitability">
          <AfLineChart months={months} testId="chart-profitability-c" formatter={(v) => `${v}%`}
                       series={[{ key: "gross_margin_ytd", label: "Gross Margin YTD", color: "#0052CC" }, { key: "net_margin_ytd", label: "Net Margin YTD", color: "#f59e0b" }]}
                       targets={[{ label: "GM target", value: t.gross_margin, color: "#0052CC" }, { label: "NM target", value: t.net_margin, color: "#f59e0b" }]} />
        </ChartCard>

        <ChartCard title="Survival" subtitle="Closing cash / monthly cost base (direct + indirect)" linkTo="/financials/cash-flow" linkLabel="Cash Flow" testId="chart-survival">
          <AfLineChart months={months} testId="chart-survival-c" formatter={(v) => `${v}`}
                       series={[{ key: "survival_months", label: "Survival (months)", color: "#0052CC" }]}
                       targets={[{ label: "Target", value: t.survival_months, color: "#f59e0b" }]} />
        </ChartCard>

        <ChartCard title="Employees" subtitle="Employee headcount (subcontractors excluded)" linkTo="/admin/people" linkLabel="People" testId="chart-employees">
          <StepChart months={months} dataKey="headcount" label="Employees" testId="chart-employees-c" />
        </ChartCard>

        <ChartCard title="Utilization" subtitle="Billable hours / capacity (employee consultants)" linkTo="/planning/resource-planning" linkLabel="Resource Planning" testId="chart-utilization">
          <AfLineChart months={months} testId="chart-utilization-c" formatter={(v) => `${v}%`}
                       series={[{ key: "utilization", label: "Utilization", color: "#0052CC" }]} />
        </ChartCard>

        <ChartCard title="Average Rate" subtitle="T&M revenue / billable hours (weighted)" linkTo="/planning/resource-planning" linkLabel="Resource Planning" testId="chart-avg-rate">
          <AfLineChart months={months} testId="chart-avg-rate-c" formatter={(v) => `${fmtSEK(v)}`}
                       series={[{ key: "average_rate", label: "Average Rate", color: "#0052CC" }]} />
        </ChartCard>

        <ChartCard title="Indirect Cost Ratio" subtitle="Cumulative YTD indirect costs / sales (incl. 6591)" linkTo="/planning/indirect-costs" linkLabel="Indirect Costs" testId="chart-indirect-ratio">
          <AfLineChart months={months} testId="chart-indirect-ratio-c" formatter={(v) => `${v}%`}
                       series={[{ key: "indirect_cost_ratio_ytd", label: "Indirect Cost Ratio YTD", color: "#0052CC" }]}
                       targets={[{ label: "Target", value: t.indirect_cost, color: "#f59e0b" }]} />
        </ChartCard>

        <ChartCard title="Average Rate — History & Outlook" subtitle="Annual, weighted (actual / expected / forecast)" testId="chart-rate-history">
          <AnnualBars rows={data.history} dataKey="average_rate" label="Average Rate" formatter={(v) => `${fmtSEK(v)}`} testId="chart-rate-history-c" />
        </ChartCard>

        <ChartCard title="Utilization — History & Outlook" subtitle="Annual, weighted hours / capacity" testId="chart-util-history">
          <AnnualBars rows={data.history} dataKey="utilization" label="Utilization" formatter={(v) => `${v}%`} testId="chart-util-history-c" />
        </ChartCard>
      </div>

      <ManagementAttention signals={data.attention} testId="company-attention" />
    </div>
  );
}
